import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { 
  Send, 
  MessageSquare, 
  Paperclip, 
  Smile, 
  Mic, 
  Type 
} from "lucide-react";
import { BASE_URL } from "../../config/api";
import toast from "react-hot-toast";
import socket from "../../socket";

/* ---------------- HELPER FOR RELATIVE DATES WITH CALENDAR DATE ---------------- */
function getRelativeDateLabel(dateString) {
  const targetDate = new Date(dateString);
  const today = new Date();
  
  // Strip hours/minutes/seconds to compare calendar days accurately
  const targetDay = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
  const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  
  const diffTime = todayDay - targetDay;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  // Format the base date (e.g., "May 20, 2026")
  const formattedDate = targetDate.toLocaleDateString("en-US", { 
    month: "short", 
    day: "numeric", 
    year: "numeric" 
  });
 
  if (diffDays === 0) return `Today, ${targetDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  if (diffDays === 1) return `Yesterday, ${targetDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  if (diffDays > 1 && diffDays <= 7) return `${diffDays} days ago, ${targetDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  
  return formattedDate;
}

export default function RemarkChat({ entityType, entityId }) {
  const authUser = JSON.parse(localStorage.getItem("authUserData"));
  const userId = authUser?.id;
  const firstName = authUser?.first_name || "";
  const lastName = authUser?.last_name || "";
  const username = `${firstName} ${lastName}`.trim();

  const [remarks, setRemarks] = useState([]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  /* ---------------- FETCH REMARKS ---------------- */
  const fetchRemarks = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}warranty/retailer-connection?retailer_id=${entityId}`
      );
      const cleaned = (res.data.data || [])
        .filter((r) => r.note && r.note.trim() !== "")
        .reverse();
      setRemarks((prev) => {
        const prevLastId = prev[prev.length - 1]?.id;
        const newLastId = cleaned[cleaned.length - 1]?.id;
        return prevLastId === newLastId ? prev : cleaned;
      });
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchRemarks();
    const interval = setInterval(fetchRemarks, 5000);
    return () => clearInterval(interval);
  }, [entityId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [remarks]);

  /* ---------------- SEND REMARK ---------------- */
  const handleSend = async () => {
    if (!message.trim()) return;
    const tempRemark = {
      id: Date.now(),
      note: message,
      created_by_id: userId,
      created_by_name: username || "You",
      created_at: new Date().toISOString(),
      optimistic: true,
    };
    setRemarks((prev) => [...prev, tempRemark]);
    setMessage("");
    try {
      setSending(true);
      const res = await axios.post(`${BASE_URL}warranty/retailer-connection`, {
        created_by_id: userId,
        created_by_name: username,
        retailer_id: entityId,
        note: message,
      });
      if (res.data?.status) {
        setRemarks((prev) =>
          prev.map((r) => (r.id === tempRemark.id ? res.data.data : r))
        );

        socket.emit("new_remark", {
            sender: username,
            message: `${username} added new remark`,
            retailer_id: entityId,
            created_at: new Date(),
          });


      } else {
        throw new Error("API returned false status");
      }
    } catch (err) {
      toast.error("Failed to send remark");
      setRemarks((prev) => prev.filter((r) => r.id !== tempRemark.id));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-[75vh] bg-[#fafafa] font-sans">
      
      {/* CHAT BODY */}
      <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
        
        {remarks.length === 0 && (
          <div className="flex flex-col items-center justify-center h-48 text-gray-400 space-y-1">
            <MessageSquare className="w-5 h-5 opacity-40" />
            <p className="text-xs font-medium">No conversation history yet</p>
          </div>
        )}

        {remarks.map((r, index) => {
          const isMine = r.created_by_id === userId;
          const time = new Date(r.created_at).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true
          }).toLowerCase();

          // Date Header Logic: Detect if date changes between consecutive remarks
          const currentRemarkLabel = getRelativeDateLabel(r.created_at);
          const previousRemarkLabel = index > 0 ? getRelativeDateLabel(remarks[index - 1].created_at) : null;
          const showDateHeader = currentRemarkLabel !== previousRemarkLabel;

          return (
            <div key={r.id} className="w-full space-y-4">
              
              {/* DYNAMIC DATE HEADER WITH EXTENDING DIVIDER LINES */}
              {showDateHeader && (
                <div className="flex items-center my-6 w-full">
                  <div className="flex-1 border-t border-gray-200"></div>
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mx-4 px-1">
                    {currentRemarkLabel}
                  </span>
                  <div className="flex-1 border-t border-gray-200"></div>
                </div>
              )}

              {/* MESSAGE ROW */}
              <div className={`flex items-start gap-3 w-full ${isMine ? "justify-end" : "justify-start"}`}>
                {/* Other User Avatar (Left Side Only) */}
                {!isMine && (
                  <div className="w-8 h-8 rounded-full flex-shrink-0 bg-gray-300 overflow-hidden border border-gray-100 shadow-sm flex items-center justify-center text-xs font-bold text-gray-600">
                    {r.created_by_name ? r.created_by_name.charAt(0).toUpperCase() : "U"}
                  </div>
                )}

                {/* Message Content Bubble Container */}
                <div className={`flex flex-col gap-1 max-w-[65%] ${isMine ? "items-end" : "items-start"}`}>
                  
                  {/* Meta Header */}
                  <div className="flex items-center gap-1.5 text-[11.5px]">
                    <span className="font-medium text-gray-600">
                      {isMine ? `${r.created_by_name} (Me)` : r.created_by_name}
                    </span>
                    <span className="text-gray-400 text-[11px]">{time}</span>
                  </div>

                  {/* Message Bubble */}
                  <div
                    className={`px-4 py-2.5 text-[13px] leading-relaxed shadow-[0_1px_2px_rgba(0,0,0,0.04)]
                      ${isMine
                        ? "bg-[#e8f1ff] text-[#1e3a8a] rounded-2xl rounded-tr-sm"
                        : "bg-[#f1f5f9] text-gray-800 rounded-2xl rounded-tl-sm"
                      }`}
                  >
                    {r.note}
                  </div>
                </div>

                {/* My Avatar (Right Side Only) */}
                {isMine && (
                  <div className="w-8 h-8 rounded-full flex-shrink-0 bg-blue-600 overflow-hidden border border-blue-100 shadow-sm flex items-center justify-center text-xs font-bold text-white">
                    M
                  </div>
                )}
              </div>

            </div>
          );
        })}

        <div ref={bottomRef} />
      </div>

      {/* FOOTER INTERFACE */}
      <div className="bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-3">
        
        {/* Unified Input Box View Container */}
        <div className="border border-[#cbd5e1] rounded-xl bg-white shadow-sm focus-within:ring-2 focus-within:ring-blue-100 focus-within:border-blue-500 transition-all flex flex-col">
          
          {/* Main Input Text Box Area */}
          <div className="p-3">
            <input
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your reply..."
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
              className="w-full text-[13.5px] text-gray-800 placeholder-gray-400 border-0 outline-none bg-transparent"
            />
          </div>

          {/* Action Tools and Send Button Bar */}
          <div className="flex items-center justify-between px-3 py-2 bg-white rounded-b-xl border-t border-gray-50">
            
            {/* Formatting Tools Left Side */}
            <div className="flex items-center gap-3.5 text-gray-400">
              <Type className="w-4 h-4 cursor-pointer hover:text-gray-600" />
              <Smile className="w-4 h-4 cursor-pointer hover:text-gray-600" />
              <Mic className="w-4 h-4 cursor-pointer hover:text-gray-600" />
              <Paperclip className="w-4 h-4 cursor-pointer hover:text-gray-600" />
            </div>

            {/* Send Button Right Side */}
            <div className="flex items-center">
              <button
                disabled={sending}
                onClick={handleSend}
                className="flex items-center gap-2 bg-[#0052cc] hover:bg-blue-700 text-white text-[12.5px] font-medium px-4 py-1.5 rounded-lg transition-colors disabled:opacity-50"
              >
                Send
                <Send className="w-3.5 h-3.5 transform rotate-45 ml-0.5" />
              </button>
            </div>

          </div>

        </div>
      </div>

    </div>
  );
}

// import React, { useEffect, useRef, useState } from "react";
// import axios from "axios";
// import { Send } from "lucide-react";
// import { BASE_URL } from "../../config/api";
// import toast from "react-hot-toast";

// export default function RemarkChat({ entityType, entityId }) {
//   const authUser = JSON.parse(localStorage.getItem("authUserData"));
//   const userId = authUser?.id;
//   const firstName = authUser?.first_name || "";
//   const lastName = authUser?.last_name || "";

//   const username = `${firstName} ${lastName}`.trim();

//   const [remarks, setRemarks] = useState([]);
//   const [message, setMessage] = useState("");
//   const [sending, setSending] = useState(false);
//   const bottomRef = useRef(null);

//   /* ---------------- FETCH REMARKS ---------------- */
 
//     const fetchRemarks = async () => {
//     try {
//         const res = await axios.get(
//         `${BASE_URL}warranty/retailer-connection?retailer_id=${entityId}`
//         );

//         const cleaned = (res.data.data || [])
//         .filter((r) => r.note && r.note.trim() !== "")
//         .reverse(); // 👈 latest goes to bottom

//         setRemarks((prev) => {
//         const prevLastId = prev[prev.length - 1]?.id;
//         const newLastId = cleaned[cleaned.length - 1]?.id;
//         return prevLastId === newLastId ? prev : cleaned;
//         });
//     } catch (err) {
//         console.error(err);
//     }
//     };



//     useEffect(() => {
//         fetchRemarks(); // initial load

//         const interval = setInterval(() => {
//             fetchRemarks(); // auto refresh
//         }, 5000); // ⏱️ every 5 seconds

//         return () => clearInterval(interval);
//         }, [entityId]);


//   /* ---------------- AUTO SCROLL ---------------- */
//   useEffect(() => {
//     bottomRef.current?.scrollIntoView({ behavior: "smooth" });
//   }, [remarks]);

//   /* ---------------- SEND REMARK ---------------- */
//   const handleSend = async () => {
//     if (!message.trim()) return;

//     const tempRemark = {
//       id: Date.now(),
//       note: message,
//       created_by_id: userId,
//       created_by_name: username || "You",
//       created_at: new Date().toISOString(),
//       optimistic: true,
//     };

//     // Optimistic UI
//     setRemarks((prev) => [...prev, tempRemark]);
//     setMessage("");

//     try {
//   setSending(true);

//   const res = await axios.post(
//     `${BASE_URL}warranty/retailer-connection`,
//     {
//       created_by_id: userId,
//       created_by_name: username,
//       retailer_id: entityId,
//       note: message,
//     }
//   );

//   if (res.data?.status) {
//     // replace optimistic message with real one
//     setRemarks((prev) =>
//       prev.map((r) =>
//         r.id === tempRemark.id ? res.data.data : r
//       )
//     );
//   } else {
//     throw new Error("API returned false status");
//   }
//     } catch (err) {
//     toast.error("Failed to send remark");

//     setRemarks((prev) =>
//         prev.filter((r) => r.id !== tempRemark.id)
//     );
//     } finally {
//     setSending(false);
//     }
//   };

//   return (
//     <div className="flex flex-col h-[70vh] border border-blue-100 rounded-2xl bg-white shadow-sm">
//       {/* CHAT BODY */}
//       <div className="flex-1 overflow-y-auto p-4 space-y-3">
//         {remarks.length === 0 && (
//           <p className="text-sm text-center text-slate-500">
//             No remarks yet
//           </p>
//         )}

//         {remarks.map((r) => {
//           const isMine = r.created_by_id === userId;

//           return (
//             <div
//               key={r.id}
//               className={`flex ${isMine ? "justify-end" : "justify-start"}`}
//             >
//               <div
//                 className={`max-w-[70%] rounded-2xl px-4 py-2 text-sm font-semibold
//                   ${
//                     isMine
//                       ? "bg-blue-600 text-white rounded-br-none"
//                       : "bg-slate-100 text-slate-800 rounded-bl-none"
//                   }
//                 `}
//               >
//                 {!isMine && (
//                   <p className="mb-1 text-[11px] font-bold text-slate-500">
//                     {r.created_by_name}
//                   </p>
//                 )}

//                 {r.note}

//                 <p className="mt-1 text-[10px] text-right opacity-70">
//                   {new Date(r.created_at).toLocaleTimeString()}
//                 </p>
//               </div>
//             </div>
//           );
//         })}

//         <div ref={bottomRef} />
//       </div>

//       {/* INPUT */}
//       <div className="border-t border-blue-100 p-3 flex gap-2">
//         <input
//           value={message}
//           onChange={(e) => setMessage(e.target.value)}
//           placeholder="Enter remark..."
//           className="flex-1 rounded-xl border border-slate-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//           onKeyDown={(e) => e.key === "Enter" && handleSend()}
//         />

//         <button
//           disabled={sending}
//           onClick={handleSend}
//           className="rounded-xl bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
//         >
//           <Send className="h-4 w-4" />
//         </button>
//       </div>
//     </div>
//   );
// }

